/**
 * Карта v3 — ортогональный роутер трасс.
 *
 * Задача: провести линию от порта до порта только по горизонтали и вертикали,
 * обходя корпуса устройств и рамки локаций, не наступая на чужие трассы и
 * проходя через точки, которые пользователь поставил руками.
 *
 * Работает в три ступени, от дешёвой к дорогой:
 *   1. Канонические формы I / L / Z / U — прямая, один угол, два, три.
 *      Закрывает подавляющее большинство пар на реальных схемах.
 *   2. A* по сетке Ханана со штрафом за поворот — когда все формы перекрыты.
 *   3. Прямой отрезок — аварийный путь, если и A* не уложился в бюджет.
 *
 * Ручные изгибы (via) не конкурируют с автоматикой: они режут маршрут на
 * участки, и каждый участок трассируется теми же тремя ступенями. Поэтому
 * «согнул в одном месте» не ломает обход препятствий на остальной длине.
 */
import { CLEAR, ROUTER } from './const';
import { simplify, near } from './math';

// ---------------------------------------------------------------- препятствия

/**
 * Препятствия для трассы.
 *
 * Корпуса устройств — ЖЁСТКИЕ: сквозь них не рисуем никогда.
 * Рамки локаций — МЯГКИЕ: пересекать границу можно, но дорого. Это принципиально.
 * Если сделать рамку жёсткой, замкнутое кольцо стенок делает НЕПРОХОДИМОЙ любую
 * связь между разными локациями — а это самый частый случай на реальной схеме
 * (кабель из серверной в цех). Тогда роутер сдаётся и рисует аварийную прямую
 * сквозь всё, что и было главной претензией к прошлым версиям карты.
 */
export function buildObstacles(s, opts = {}) {
  const hard = [];
  const walls = [];
  for (const n of s.nodes.values()) {
    if (n.hidden) continue;
    hard.push({
      x: n.x - CLEAR, y: n.y - CLEAR,
      w: n.w + CLEAR * 2, h: n.h + CLEAR * 2,
      id: n.id,
    });
  }
  if (opts.avoidContainers !== false) {
    for (const c of s.conts.values()) {
      const t = 2;
      walls.push({ x: c.x - t, y: c.y - t, w: c.w + t * 2, h: t * 2, cont: c.id });
      walls.push({ x: c.x - t, y: c.y + c.h - t, w: c.w + t * 2, h: t * 2, cont: c.id });
      walls.push({ x: c.x - t, y: c.y - t, w: t * 2, h: c.h + t * 2, cont: c.id });
      walls.push({ x: c.x + c.w - t, y: c.y - t, w: t * 2, h: c.h + t * 2, cont: c.id });
    }
  }
  return { hard, walls, all: hard.concat(walls) };
}

/** Осевой отрезок против прямоугольника. */
function segHitsRect(x1, y1, x2, y2, r) {
  const minx = Math.min(x1, x2), maxx = Math.max(x1, x2);
  const miny = Math.min(y1, y2), maxy = Math.max(y1, y2);
  return minx < r.x + r.w && maxx > r.x && miny < r.y + r.h && maxy > r.y;
}

/** Жёсткая проверка: упирается ли отрезок в корпус устройства. */
function segBlocked(x1, y1, x2, y2, obs, skip) {
  const hard = obs.hard || obs;
  for (let i = 0; i < hard.length; i++) {
    const r = hard[i];
    if (skip && (r.id === skip.a || r.id === skip.b)) continue;
    if (segHitsRect(x1, y1, x2, y2, r)) return true;
  }
  return false;
}

/** Сколько границ локаций пересекает отрезок — идёт в стоимость, не в запрет. */
function wallCrossings(x1, y1, x2, y2, obs, skip) {
  const walls = obs.walls || [];
  let n = 0;
  for (let i = 0; i < walls.length; i++) {
    const r = walls[i];
    if (skip && skip.contOpen && skip.contOpen.has(r.cont)) continue;
    if (segHitsRect(x1, y1, x2, y2, r)) n++;
  }
  return n;
}

function pathBlocked(pts, obs, skip) {
  for (let i = 0; i + 3 < pts.length; i += 2) {
    if (segBlocked(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], obs, skip)) return true;
  }
  return false;
}

function pathWallCost(pts, obs, skip) {
  let n = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    n += wallCrossings(pts[i], pts[i + 1], pts[i + 2], pts[i + 3], obs, skip);
  }
  return n * ROUTER.WALL_COST;
}

// ------------------------------------------------------- канонические формы

/**
 * Кандидаты-формы между двумя точками с заданными направлениями выхода.
 * dirA/dirB — единичные векторы нормалей портов; трасса обязана начинаться и
 * заканчиваться движением вдоль них, иначе линия войдёт в корпус боком.
 */
function shapeCandidates(ax, ay, bx, by, dirA, dirB) {
  const out = [];
  const horizA = dirA.x !== 0, horizB = dirB.x !== 0;

  // I — прямая, если точки на одной оси и направления это позволяют
  if (near(ay, by, 1.5) && horizA && horizB) out.push([ax, ay, bx, by]);
  if (near(ax, bx, 1.5) && !horizA && !horizB) out.push([ax, ay, bx, by]);

  // L — один угол
  if (horizA && !horizB) out.push([ax, ay, bx, ay, bx, by]);
  if (!horizA && horizB) out.push([ax, ay, ax, by, bx, by]);

  // Z — два угла, переход посередине
  if (horizA && horizB) {
    const mx = (ax + bx) / 2;
    out.push([ax, ay, mx, ay, mx, by, bx, by]);
  }
  if (!horizA && !horizB) {
    const my = (ay + by) / 2;
    out.push([ax, ay, ax, my, bx, my, bx, by]);
  }

  // U — три угла, обход «вокруг». Выносов несколько: узкий проход между
  // соседними корпусами часто не пролезает, а обход подальше — свободен.
  // Дешёвая проверка нескольких вариантов почти всегда избавляет от
  // аварийного пути, который режет схему насквозь.
  for (const R of [46, 92, 150, 230]) {
    if (horizA && horizB) {
      for (const dy of [-R, R]) {
        const y = (ay + by) / 2 + dy;
        out.push([ax, ay, ax + dirA.x * 8, ay, ax + dirA.x * 8, y, bx + dirB.x * 8, y, bx + dirB.x * 8, by, bx, by]);
      }
    }
    if (!horizA && !horizB) {
      for (const dx of [-R, R]) {
        const x = (ax + bx) / 2 + dx;
        out.push([ax, ay, ax, ay + dirA.y * 8, x, ay + dirA.y * 8, x, by + dirB.y * 8, bx, by + dirB.y * 8, bx, by]);
      }
    }
    // смешанные направления: колено с выносом в сторону
    if (horizA && !horizB) {
      out.push([ax, ay, ax + dirA.x * R, ay, ax + dirA.x * R, by, bx, by]);
      out.push([ax, ay, bx, ay, bx, by]);
    }
    if (!horizA && horizB) {
      out.push([ax, ay, ax, ay + dirA.y * R, bx, ay + dirA.y * R, bx, by]);
      out.push([ax, ay, ax, by, bx, by]);
    }
  }

  // смешанные направления — второй вариант L через противоположный угол
  if (horizA && !horizB) out.push([ax, ay, ax + dirA.x * 40, ay, ax + dirA.x * 40, by, bx, by]);
  if (!horizA && horizB) out.push([ax, ay, ax, ay + dirA.y * 40, bx, ay + dirA.y * 40, bx, by]);

  return out;
}

function pathCost(pts) {
  let len = 0, turns = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    len += Math.abs(pts[i + 2] - pts[i]) + Math.abs(pts[i + 3] - pts[i + 1]);
    if (i + 5 < pts.length) turns++;
  }
  return len + turns * ROUTER.TURN_COST;
}

/** Первая ступень: перебираем формы, берём самую дешёвую чистую. */
function routeByShapes(ax, ay, bx, by, dirA, dirB, obs, skip) {
  const cands = shapeCandidates(ax, ay, bx, by, dirA, dirB);
  let best = null, bestCost = Infinity;
  for (const c of cands) {
    const pts = simplify(c);
    if (pathBlocked(pts, obs, skip)) continue;          // корпуса — запрет
    const cost = pathCost(pts) + pathWallCost(pts, obs, skip);  // рамки — цена
    if (cost < bestCost) { bestCost = cost; best = pts; }
  }
  return best;
}

// ------------------------------------------------------------ A* по Ханану

/**
 * Сетка Ханана: только те координаты, где реально может пройти оптимальная
 * ортогональная трасса — грани препятствий с зазором плюс концы маршрута.
 * Это даёт десятки координат вместо тысяч ячеек равномерной сетки.
 */
function hananGrid(ax, ay, bx, by, obs, pad) {
  // ТОЛЬКО корпуса: стенки локаций мягкие, обходить их по сетке не нужно,
  // а вчетверо больше координат превращают A* в секундные паузы.
  const obstacles = obs.hard || obs;
  const xs = new Set([ax, bx]);
  const ys = new Set([ay, by]);
  for (const r of obstacles) {
    xs.add(r.x - pad); xs.add(r.x + r.w + pad);
    ys.add(r.y - pad); ys.add(r.y + r.h + pad);
  }
  const X = [...xs].filter((v) => isFinite(v)).sort((p, q) => p - q);
  const Y = [...ys].filter((v) => isFinite(v)).sort((p, q) => p - q);
  // слить почти совпадающие координаты — иначе сетка распухает без пользы
  const merge = (arr) => {
    const out = [];
    for (const v of arr) if (!out.length || v - out[out.length - 1] > 2) out.push(v);
    return out;
  };
  return { X: merge(X), Y: merge(Y) };
}

const idxOf = (arr, v) => {
  let lo = 0, hi = arr.length - 1, best = 0, bd = Infinity;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const d = Math.abs(arr[m] - v);
    if (d < bd) { bd = d; best = m; }
    if (arr[m] < v) lo = m + 1; else hi = m - 1;
  }
  return best;
};

/** Вторая ступень: A* по (i, j, направление) со штрафом за поворот. */
function routeByAstar(ax, ay, bx, by, dirA, dirB, obs, skip) {
  const { X, Y } = hananGrid(ax, ay, bx, by, obs, CLEAR);
  const nx = X.length, ny = Y.length;
  if (nx * ny > 14000) return null;   // дальше дешевле отдать простую форму

  const si = idxOf(X, ax), sj = idxOf(Y, ay);
  const ti = idxOf(X, bx), tj = idxOf(Y, by);

  const key = (i, j, d) => ((j * nx + i) * 4 + d);
  const H = (i, j) => Math.abs(X[i] - X[ti]) + Math.abs(Y[j] - Y[tj]);

  const g = new Map(), came = new Map();
  const open = [];                                   // бинарная куча на массиве
  const push = (f, k) => {
    open.push({ f, k });
    let c = open.length - 1;
    while (c > 0) {
      const p = (c - 1) >> 1;
      if (open[p].f <= open[c].f) break;
      const t = open[p]; open[p] = open[c]; open[c] = t; c = p;
    }
  };
  const pop = () => {
    const top = open[0], last = open.pop();
    if (open.length) {
      open[0] = last;
      let c = 0;
      for (;;) {
        const l = c * 2 + 1, r = l + 1;
        let m = c;
        if (l < open.length && open[l].f < open[m].f) m = l;
        if (r < open.length && open[r].f < open[m].f) m = r;
        if (m === c) break;
        const t = open[m]; open[m] = open[c]; open[c] = t; c = m;
      }
    }
    return top;
  };

  // 0:вправо 1:вниз 2:влево 3:вверх
  const DIRS = [[1, 0], [0, 1], [-1, 0], [0, -1]];
  const startDir = dirA.x > 0 ? 0 : dirA.x < 0 ? 2 : dirA.y > 0 ? 1 : 3;
  const endDir = dirB.x > 0 ? 2 : dirB.x < 0 ? 0 : dirB.y > 0 ? 3 : 1;

  const s0 = key(si, sj, startDir);
  g.set(s0, 0); push(H(si, sj), s0);

  let expanded = 0, found = null;
  while (open.length && expanded < ROUTER.MAX_NODES) {
    const cur = pop();
    const ck = cur.k;
    const d = ck & 3, cell = ck >> 2, i = cell % nx, j = (cell / nx) | 0;
    expanded++;

    if (i === ti && j === tj && d === endDir) { found = ck; break; }

    const gc = g.get(ck);
    if (gc === undefined) continue;

    for (let nd = 0; nd < 4; nd++) {
      if ((nd + 2) % 4 === d) continue;                       // разворот назад запрещён
      const ni = i + DIRS[nd][0], nj = j + DIRS[nd][1];
      if (ni < 0 || nj < 0 || ni >= nx || nj >= ny) continue;

      const x1 = X[i], y1 = Y[j], x2 = X[ni], y2 = Y[nj];
      if (segBlocked(x1, y1, x2, y2, obs, skip)) continue;

      const step = Math.abs(x2 - x1) + Math.abs(y2 - y1);
      const wall = wallCrossings(x1, y1, x2, y2, obs, skip) * ROUTER.WALL_COST;
      const cost = gc + step + wall + (nd === d ? 0 : ROUTER.TURN_COST);
      const nk = key(ni, nj, nd);
      if (g.has(nk) && g.get(nk) <= cost) continue;
      g.set(nk, cost); came.set(nk, ck);
      push(cost + H(ni, nj), nk);
    }
  }

  if (found === null) {
    // цель могла быть достигнута с другим направлением входа — принимаем любое
    let bestK = null, bestG = Infinity;
    for (let d = 0; d < 4; d++) {
      const k = key(ti, tj, d);
      if (g.has(k) && g.get(k) < bestG) { bestG = g.get(k); bestK = k; }
    }
    if (bestK === null) return null;
    found = bestK;
  }

  const pts = [];
  let k = found;
  const guard = new Set();
  while (k !== undefined && !guard.has(k)) {
    guard.add(k);
    const cell = k >> 2, i = cell % nx, j = (cell / nx) | 0;
    pts.unshift(X[i], Y[j]);
    k = came.get(k);
  }
  return pts.length >= 4 ? simplify(pts) : null;
}

// ------------------------------------------------------------------- via

/**
 * Ручные изгибы. Резолвим якорь: точка может быть привязана к мировым
 * координатам или к устройству/локации — тогда она едет вместе с носителем,
 * и изгиб не «отваливается» при перетаскивании.
 */
export function resolveVia(v, s) {
  const a = v.anchor || { mode: 'world' };
  if (a.mode === 'node') {
    const n = s.nodes.get(a.ref);
    if (n) return { x: n.x + n.w / 2 + (a.dx || 0), y: n.y + n.h / 2 + (a.dy || 0) };
  } else if (a.mode === 'loc') {
    const c = s.conts.get(a.ref);
    if (c) return { x: c.x + (a.dx || 0), y: c.y + (a.dy || 0) };
  }
  return { x: v.x, y: v.y };
}

/** Куда «смотрит» конец связи — на первый изгиб, если он есть, иначе на второй конец. */
export function edgeAnchor(s) {
  return (e, end) => {
    const other = end === 'a' ? s.nodes.get(e.b) : s.nodes.get(e.a);
    const vias = e.vias || [];
    if (vias.length) {
      const v = end === 'a' ? vias[0] : vias[vias.length - 1];
      const p = resolveVia(v, s);
      if (isFinite(p.x) && isFinite(p.y)) return p;
    }
    if (!other) return { x: 0, y: 0 };
    return { x: other.x + other.w / 2, y: other.y + other.h / 2 };
  };
}

// ------------------------------------------------------------- оркестрация

function normal(side) {
  if (side === 'N') return { x: 0, y: -1 };
  if (side === 'S') return { x: 0, y: 1 };
  if (side === 'W') return { x: -1, y: 0 };
  return { x: 1, y: 0 };
}

/** Один участок: A → B с заданными направлениями. Три ступени по очереди. */
function routeSegment(ax, ay, bx, by, dirA, dirB, obs, skip, flags) {
  const byShape = routeByShapes(ax, ay, bx, by, dirA, dirB, obs, skip);
  if (byShape) return byShape;
  const byAstar = routeByAstar(ax, ay, bx, by, dirA, dirB, obs, skip);
  if (byAstar) return byAstar;
  // Ни форма, ни поиск не нашли чистого пути. Рисуем букву Г, но помечаем
  // трассу как приблизительную: пользователь должен видеть, что здесь схема
  // не сошлась, а не гадать, почему линия режет чужой корпус.
  if (flags) flags.approx = true;
  return simplify([ax, ay, bx, ay, bx, by]);
}

/**
 * Полный маршрут одной связи: порт → [изгибы] → порт.
 * Каждый участок трассируется отдельно, поэтому ручная точка не отменяет
 * обход препятствий на остальных участках.
 */
export function routeEdge(e, s, obs) {
  if (!e.aPt || !e.bPt) { e.route = null; e.approx = false; return; }

  if (e.mode === 'manual' && Array.isArray(e.pts) && e.pts.length >= 4) {
    e.route = { pts: e.pts.slice() };
    return;
  }

  const skip = { a: e.a, b: e.b, contOpen: new Set() };
  // Рамки локаций, внутри которых лежат ОБА конца, не считаем пересечёнными:
  // кабель внутри одной комнаты границу этой комнаты не переходит. Родительские
  // локации тоже — иначе связь внутри цеха «платила» бы за рамку завода.
  const A = s.nodes.get(e.a), B = s.nodes.get(e.b);
  const chain = (locId) => {
    const out = [];
    let cur = locId, guard = 0;
    while (cur && guard++ < 16) { out.push(cur); cur = s.conts.get(cur)?.parentId; }
    return out;
  };
  if (A?.locId && B?.locId) {
    const ca = chain(A.locId), cb = new Set(chain(B.locId));
    for (const id of ca) if (cb.has(id)) skip.contOpen.add(id);
  }
  const flags = { approx: false };

  const dirA = normal(e.aSide), dirB = normal(e.bSide);
  const vias = (e.vias || []).map((v) => resolveVia(v, s)).filter((p) => isFinite(p.x) && isFinite(p.y));

  const pts = [e.aPt.x, e.aPt.y, e.aEsc.x, e.aEsc.y];

  if (!vias.length) {
    const mid = routeSegment(e.aEsc.x, e.aEsc.y, e.bEsc.x, e.bEsc.y, dirA, dirB, obs, skip, flags);
    for (let i = 2; i < mid.length; i += 2) pts.push(mid[i], mid[i + 1]);
  } else {
    let curX = e.aEsc.x, curY = e.aEsc.y, curDir = dirA;
    for (let k = 0; k < vias.length; k++) {
      const v = vias[k];
      // направление подхода к изгибу — свободное, поэтому берём «любое»,
      // подставляя нормаль по преобладающей оси
      const dx = v.x - curX, dy = v.y - curY;
      const inDir = Math.abs(dx) >= Math.abs(dy)
        ? { x: dx >= 0 ? 1 : -1, y: 0 }
        : { x: 0, y: dy >= 0 ? 1 : -1 };
      const seg = routeSegment(curX, curY, v.x, v.y, curDir, { x: -inDir.x, y: -inDir.y }, obs, skip, flags);
      for (let i = 2; i < seg.length; i += 2) pts.push(seg[i], seg[i + 1]);
      curX = v.x; curY = v.y; curDir = inDir;
    }
    const last = routeSegment(curX, curY, e.bEsc.x, e.bEsc.y, curDir, dirB, obs, skip, flags);
    for (let i = 2; i < last.length; i += 2) pts.push(last[i], last[i + 1]);
  }

  pts.push(e.bPt.x, e.bPt.y);
  e.route = { pts: simplify(pts) };
  e.approx = flags.approx;
  // Координаты ручных точек на готовом маршруте. Разведение параллельных трасс
  // обязано обходить эти места стороной: если сдвинуть сегмент, на котором сидит
  // изгиб, линия уедет, а рыжая ручка останется висеть в стороне от неё —
  // именно это и выглядит как «точки за пределами линии».
  e.viaAt = vias.map((v) => ({ x: v.x, y: v.y }));
}

// --------------------------------------------------- разведение параллелей

/**
 * Трассы, идущие по одной координате, накладываются в одну линию и выглядят
 * как одна. Разводим их на PITCH: группируем сегменты по оси и координате,
 * внутри группы раздвигаем симметрично, сохраняя порядок по перпендикуляру —
 * так кабели не перекрещиваются лишний раз.
 */
export function nudgeParallel(s, obs, pitch = 13) {
  const groups = new Map();

  for (const e of s.edges.values()) {
    const r = e.route;
    if (!r || !r.pts || r.pts.length < 4) continue;
    const pts = r.pts;
    // крайние сегменты (вылеты из портов) не двигаем — они держат перпендикуляр
    for (let i = 2; i + 3 < pts.length - 2; i += 2) {
      const x1 = pts[i], y1 = pts[i + 1], x2 = pts[i + 2], y2 = pts[i + 3];
      const horiz = near(y1, y2, 0.6);
      const vert = near(x1, x2, 0.6);
      if (!horiz && !vert) continue;
      const axis = horiz ? 'h' : 'v';
      const coord = Math.round((horiz ? y1 : x1) / 4) * 4;
      const from = horiz ? Math.min(x1, x2) : Math.min(y1, y2);
      const to = horiz ? Math.max(x1, x2) : Math.max(y1, y2);
      // сегмент с ручной точкой на конце не двигаем
      const anchors = e.viaAt || [];
      const touchesVia = anchors.some((p) =>
        (Math.abs(p.x - x1) < 3 && Math.abs(p.y - y1) < 3) ||
        (Math.abs(p.x - x2) < 3 && Math.abs(p.y - y2) < 3));
      if (touchesVia) continue;

      const k = `${axis}:${coord}`;
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push({ e, i, axis, coord, from, to });
    }
  }

  for (const [, members] of groups) {
    if (members.length < 2) continue;

    // разводить есть смысл только у тех, кто реально перекрывается по длине:
    // две трассы на одной координате, но в разных концах схемы, друг другу
    // не мешают, и сдвигать их — только портить геометрию
    const overlaps = members.some((m, i) =>
      members.some((o, j) => j !== i && m.from < o.to - 4 && o.from < m.to - 4));
    if (!overlaps) continue;

    members.sort((p, q) => p.e.id - q.e.id);
    const n = members.length;
    members.forEach((m, k) => {
      const off = (k - (n - 1) / 2) * pitch;
      if (Math.abs(off) < 0.01) return;
      const pts = m.e.route.pts;
      const x1 = pts[m.i], y1 = pts[m.i + 1], x2 = pts[m.i + 2], y2 = pts[m.i + 3];
      const skip = { a: m.e.a, b: m.e.b };

      // ОБЯЗАТЕЛЬНАЯ ПОВТОРНАЯ ПРОВЕРКА. Роутер только что аккуратно обошёл
      // корпуса с зазором; если слепо сдвинуть сегмент на 20-30 единиц, трасса
      // въедет прямо в соседнее устройство — и это будет уже после того, как
      // маршрут признан чистым. Пробуем полное смещение, потом половину, потом
      // четверть; не нашли свободного места — оставляем как было.
      for (const factor of [1, 0.5, 0.25, 0]) {
        const d = off * factor;
        if (Math.abs(d) < 0.01) break;
        const nx1 = m.axis === 'h' ? x1 : x1 + d;
        const ny1 = m.axis === 'h' ? y1 + d : y1;
        const nx2 = m.axis === 'h' ? x2 : x2 + d;
        const ny2 = m.axis === 'h' ? y2 + d : y2;
        if (segBlocked(nx1, ny1, nx2, ny2, obs, skip)) continue;
        pts[m.i] = nx1; pts[m.i + 1] = ny1; pts[m.i + 2] = nx2; pts[m.i + 3] = ny2;
        break;
      }
    });
  }

  // после сдвига концы соседних сегментов разъехались — сшиваем углы
  for (const e of s.edges.values()) {
    const r = e.route;
    if (!r || r.pts.length < 6) continue;
    const p = r.pts;
    for (let i = 2; i + 3 < p.length; i += 2) {
      const prevH = near(p[i - 1], p[i + 1], 0.6);
      if (prevH) p[i - 1] = p[i + 1]; else p[i - 2] = p[i];
    }
    r.pts = simplify(p);
  }
}

/** Полный проход: порты уже посчитаны, строим все маршруты и разводим их. */
export function routeAll(s, opts = {}) {
  const obs = buildObstacles(s, opts);
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  let routed = 0, approx = 0;
  for (const e of s.edges.values()) {
    routeEdge(e, s, obs);
    routed++;
    if (e.approx) approx++;
  }
  if (opts.nudge !== false) nudgeParallel(s, obs);
  const t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  return { routed, approx, ms: t1 - t0 };
}
